import React, { useState, useEffect, useRef } from 'react';
import { Tag, Space, Button, message, Modal, Row, Col, Typography, Popconfirm } from 'antd';
import { EyeOutlined, ShopOutlined, EditOutlined, DeleteOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { Shop, ShopLabels } from '../../types/entity/shop';
import { MainBody } from '../../types/entity/main-body';
import { shopApi } from '../../services/manager/shop';
import { mainBodyApi } from '../../services/manager/main-body';
import { useMainBodyMap } from '../../services/store/organization.store';
import { UpsertModal, type FormFieldConfig } from '../../components/Templates/UpsertModal';
import List, { ListRef } from '../../components/Templates/List';
import type { FieldConfig, ForgeQuery } from '../../components/ForgeQueryBuilder';

const { Text } = Typography;

const Shops: React.FC = () => {
  const { mainBodyMap } = useMainBodyMap();
  const [mainBodies, setMainBodies] = useState<MainBody[]>([]);
  const listRef = useRef<ListRef>(null);
  const [modalState, setModalState] = useState<{ open: boolean; mode: 'create' | 'edit'; record: Shop | null }>({
    open: false,
    mode: 'create',
    record: null
  });

  useEffect(() => {
    mainBodyApi.getAll().then(res => {
      if (res.code === 200) setMainBodies(res.data.items || []);
    });
  }, []);

  const queryFields: FieldConfig[] = [
    { key: 'name', label: ShopLabels.name, type: 'string' },
    { key: 'code', label: ShopLabels.code, type: 'string' },
    { key: 'main_body_id', label: ShopLabels.main_body_id, type: 'select', options: mainBodies.map(mb => ({ label: mb.name || '', value: mb.id })) },
    { key: 'status', label: ShopLabels.status, type: 'select', options: [{ label: '启用', value: 1 }, { label: '禁用', value: 0 }] },
    { key: 'platform', label: ShopLabels.platform, type: 'string' },
  ];

  const formFields: FormFieldConfig[] = [
    { name: 'name', label: ShopLabels.name, type: 'text', required: true, span: 12 },
    { name: 'code', label: ShopLabels.code, type: 'text', required: true, rules: [{ max: 50 }], span: 12 },
    { name: 'main_body_id', label: ShopLabels.main_body_id, type: 'select', required: true, options: mainBodies.map(mb => ({ label: `${mb.name} (${mb.code || 'N/A'})`, value: mb.id })), span: 12 },
    { name: 'status', label: ShopLabels.status, type: 'select', required: true, initialValue: 1, options: [{ label: '启用', value: 1 }, { label: '禁用', value: 0 }], span: 12 },
    { name: 'server_id', label: ShopLabels.server_id, type: 'number', span: 12 },
    { name: 'platform', label: ShopLabels.platform, type: 'text', rules: [{ max: 50 }], span: 12 },
    { name: 'contact_phone', label: ShopLabels.contact_phone, type: 'text', rules: [{ max: 20 }], span: 12 },
    { name: 'address', label: ShopLabels.address, type: 'text', rules: [{ max: 500 }], span: 12 },
    { name: 'description', label: ShopLabels.description, type: 'textarea', rules: [{ max: 500 }], span: 24 },
  ];

  const columns: ColumnsType<Shop> = [
    { title: 'ID', dataIndex: 'id', width: 80, fixed: 'left' },
    { title: ShopLabels.name, dataIndex: 'name', width: 150, fixed: 'left', render: (t) => <Text strong style={{ color: '#1890ff' }}><ShopOutlined style={{ marginRight: 4 }} />{t}</Text> },
    { title: ShopLabels.code, dataIndex: 'code', width: 120, render: (t) => <Text code>{t}</Text> },
    { title: ShopLabels.main_body_id, dataIndex: 'main_body', width: 150, render: (_, r) => {
        const mb = mainBodyMap.get(r.main_body_id as number);
        return <div><Tag color="blue">{mb?.name || '-'}</Tag>{mb?.code && <Text type="secondary" style={{ fontSize: '12px' }}>{mb.code}</Text>}</div>;
    }},
    { title: ShopLabels.description, dataIndex: 'description', width: 200, ellipsis: true },
    { title: ShopLabels.status, dataIndex: 'status', width: 100, render: (s) => <Tag color={s === 1 ? 'green' : 'red'}>{s === 1 ? '启用' : '禁用'}</Tag> },
    { title: '创建时间', dataIndex: 'created_at', width: 180, render: (t) => new Date(t).toLocaleString('zh-CN') },
    {
      title: '操作',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" icon={<EyeOutlined />} size="small" onClick={() => handleView(record)}>查看</Button>
          <Button type="link" icon={<EditOutlined />} size="small" onClick={() => setModalState({ open: true, mode: 'edit', record })}>编辑</Button>
          <Popconfirm title="确定删除?" onConfirm={() => handleDelete(record)}>
            <Button type="link" icon={<DeleteOutlined />} size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const handleDelete = async (record: Shop) => {
    try {
      await shopApi.deleteById(String(record.id));
      message.success('删除成功');
      listRef.current?.reload();
    } catch (error: any) {
      message.error(error.message || '删除失败');
    }
  };

  const handleView = (record: Shop) => {
    Modal.info({
      title: '店铺详情',
      width: 700,
      content: (
        <div style={{ marginTop: 16 }}>
          <Row gutter={[16, 8]}>
            <Col span={8}><strong>ID:</strong></Col>
            <Col span={16}>{record.id}</Col>
          </Row>
          <Row gutter={[16, 8]}>
            <Col span={8}><strong>{ShopLabels.name}:</strong></Col>
            <Col span={16}>{record.name}</Col>
          </Row>
          <Row gutter={[16, 8]}>
            <Col span={8}><strong>{ShopLabels.code}:</strong></Col>
            <Col span={16}>{record.code}</Col>
          </Row>
          <Row gutter={[16, 8]}>
            <Col span={8}><strong>{ShopLabels.main_body_id}:</strong></Col>
            <Col span={16}>
              <Tag color="blue">{record.main_body?.name || '-'}</Tag>
              {record.main_body?.code && (
                <Text type="secondary">({record.main_body.code})</Text>
              )}
            </Col>
          </Row>
          <Row gutter={[16, 8]}>
            <Col span={8}><strong>{ShopLabels.description}:</strong></Col>
            <Col span={16}>{record.description || '-'}</Col>
          </Row>
          <Row gutter={[16, 8]}>
            <Col span={8}><strong>{ShopLabels.status}:</strong></Col>
            <Col span={16}>
              <Tag color={record.status === 1 ? 'green' : 'red'}>
                {record.status === 1 ? '启用' : '禁用'}
              </Tag>
            </Col>
          </Row>
          <Row gutter={[16, 8]}>
            <Col span={8}><strong>{ShopLabels.server_id}:</strong></Col>
            <Col span={16}>{record.server_id || '-'}</Col>
          </Row>
          <Row gutter={[16, 8]}>
            <Col span={8}><strong>{ShopLabels.platform}:</strong></Col>
            <Col span={16}>{record.platform || '-'}</Col>
          </Row>
          <Row gutter={[16, 8]}>
            <Col span={8}><strong>{ShopLabels.contact_phone}:</strong></Col>
            <Col span={16}>{record.contact_phone || '-'}</Col>
          </Row>
          <Row gutter={[16, 8]}>
            <Col span={8}><strong>{ShopLabels.address}:</strong></Col>
            <Col span={16}>{record.address || '-'}</Col>
          </Row>
          <Row gutter={[16, 8]}>
            <Col span={8}><strong>创建时间:</strong></Col>
            <Col span={16}>{new Date(record.created_at || '').toLocaleString('zh-CN')}</Col>
          </Row>
          <Row gutter={[16, 8]}>
            <Col span={8}><strong>更新时间:</strong></Col>
            <Col span={16}>{new Date(record.updated_at || '').toLocaleString('zh-CN')}</Col>
          </Row>
        </div>
      ),
    });
  };

  return (
    <>
      <List<Shop>
        ref={listRef}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>店铺管理</span>
            <Space>
               <Button icon={<ReloadOutlined />} onClick={() => listRef.current?.reload()}>刷新</Button>
               <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalState({ open: true, mode: 'create', record: null })}>
                 新增店铺
               </Button>
            </Space>
          </div>
        }
        queryFields={queryFields}
        fetcher={async (params) => {
          const res = await shopApi.list(params);
          return { rows: res.data.items, pagination: res.data.pagination };
        }}
        buildParams={(q) => {
           const params: any = { page: q.page, pageSize: q.pageSize };
           if (q.filter) {
               Object.entries(q.filter).forEach(([key, val]) => {
                  if (val && typeof val === 'object' && 'value' in val) params[key] = val.value;
               });
           }
           return params;
        }}
        columns={columns}
        rowKey={(r) => String(r.id)}
        tableProps={{ scroll: { x: 'max-content' } }}
      />
      <UpsertModal<Shop>
        title={modalState.mode === 'create' ? '新增店铺' : '编辑店铺'}
        open={modalState.open}
        mode={modalState.mode}
        record={modalState.record}
        onCancel={() => setModalState({ ...modalState, open: false })}
        onSuccess={() => {
          setModalState({ ...modalState, open: false });
          listRef.current?.reload();
        }}
        createApi={(data) => shopApi.create(data)}
        updateApi={(id, data) => shopApi.updateById(id, data)}
        formFields={formFields}
        width={700}
      />
    </>
  );
}

export default Shops;
